import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Lenis from "lenis";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, Aperture } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { LanguageSelector } from "@/components/LanguageSelector";
import { AnimatedTitle } from "@/slides/SlideRenderer";
import { IMG } from "@/content/images";

const EASE = [0.85, 0, 0.15, 1];

const CardEntry = ({ card, onClick, span, i }) => (
  <motion.button
    onClick={onClick}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, ease: EASE, delay: i * 0.08 }}
    data-testid={`home-card-${card.key}`}
    className={`group relative flex flex-col justify-between overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-8 text-left backdrop-blur-sm transition-all hover:border-champagne/50 hover:bg-white/[0.05] ${span}`}
  >
    <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-champagne/[0.06] blur-3xl transition-all duration-500 group-hover:bg-champagne/[0.14]" />
    <div className="flex items-start justify-between">
      <span className="font-serif text-5xl text-champagne/40 transition-colors group-hover:text-champagne/70">{card.index}</span>
      <span className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-cream/60 transition-all group-hover:border-champagne group-hover:text-champagne group-hover:rotate-45">
        <ArrowUpRight className="h-5 w-5" strokeWidth={1.5} />
      </span>
    </div>
    <div className="mt-16">
      <div className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-cream/50">{card.meta}</div>
      <h3 className="mb-3 font-serif text-2xl leading-tight text-white sm:text-3xl">{card.title}</h3>
      <p className="max-w-md text-sm leading-relaxed text-cream/65">{card.desc}</p>
    </div>
  </motion.button>
);

export default function HomeMenu() {
  const navigate = useNavigate();
  const { t } = useApp();
  const heroRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const go = (key) => {
    if (key === "onepager") navigate("/one-pager");
    else navigate(`/deck/${key}`);
  };

  const spans = {
    short: "md:col-span-7 md:row-span-1",
    full: "md:col-span-5 md:row-span-2",
    tutorial: "md:col-span-4",
    onepager: "md:col-span-3",
  };

  return (
    <div className="relative min-h-screen w-full bg-navy-800" data-testid="home-menu">
      {/* Top bar */}
      <header className="fixed left-0 right-0 top-0 z-50 flex items-center justify-between px-6 py-5 sm:px-10">
        <div className="flex items-center gap-2.5">
          <Aperture className="h-6 w-6 text-champagne" strokeWidth={1.5} />
          <span className="font-serif text-xl tracking-tight text-cream">iFotoLife</span>
        </div>
        <LanguageSelector />
      </header>

      {/* Hero */}
      <section ref={heroRef} className="relative flex min-h-screen items-center overflow-hidden px-6 pt-28 sm:px-10 lg:px-20">
        <motion.div style={{ y: imgY, scale: imgScale }} className="pointer-events-none absolute right-0 top-0 h-full w-full lg:w-[55%]">
          <img src={IMG.resortPool} alt="" className="h-full w-full object-cover opacity-40 lg:opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-800 via-navy-800/70 to-transparent lg:via-navy-800/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-800 to-transparent" />
        </motion.div>

        <motion.div style={{ y: textY }} className="relative z-10 max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="mb-6 flex items-center gap-3"
          >
            <span className="h-px w-8 bg-champagne" />
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-champagne">{t.home.eyebrow}</span>
          </motion.div>
          <AnimatedTitle text={t.home.title} className="text-5xl font-medium leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-7xl" />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.7 }}
            className="mt-8 max-w-xl text-lg font-light leading-relaxed text-cream/75"
          >
            {t.home.intro}
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 1 }}
            className="mt-10 text-xs uppercase tracking-[0.2em] text-cream/40"
          >
            {t.ui.hint}
          </motion.div>
        </motion.div>
      </section>

      {/* Editorial marquee */}
      <div className="relative border-y border-white/5 py-6">
        <Marquee speed={30} gradient={false} autoFill>
          {t.home.taglines.map((tag, i) => (
            <span key={i} className="stroke-text mx-8 font-serif text-5xl sm:text-7xl">{tag}</span>
          ))}
        </Marquee>
      </div>

      {/* Bento grid of journeys */}
      <section className="relative px-6 py-24 sm:px-10 lg:px-20">
        <div className="mb-12 flex items-end justify-between">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-champagne">{t.home.cardsTitle}</span>
            <h2 className="mt-3 font-serif text-4xl tracking-tight text-white sm:text-5xl">{t.home.taglines[1]}</h2>
          </div>
        </div>
        <div className="grid auto-rows-fr grid-cols-1 gap-5 md:grid-cols-12">
          {t.home.cards.map((card, i) => (
            <CardEntry key={card.key} card={card} onClick={() => go(card.key)} span={spans[card.key]} i={i} />
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 px-6 py-10 sm:px-10 lg:px-20">
        <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
          <div className="flex items-center gap-2.5">
            <Aperture className="h-5 w-5 text-champagne" strokeWidth={1.5} />
            <span className="font-serif text-cream">iFotoLife</span>
          </div>
          <p className="font-serif text-sm italic text-cream/50">{t.home.taglines[0]}</p>
          <span className="text-xs text-cream/40">ifotolife.shop</span>
        </div>
      </footer>
    </div>
  );
}
